import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, PlusCircle, Loader2, MapPin, Mail, Phone, ArrowRight,
  FileText, BadgeCheck, Sparkles, ClipboardList, Search, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { altioraToast, toastApiError } from "@/components/altiora/toast";
import {
  PageShell, PageHeader, Card, NAVY, CHAMPAGNE,
} from "@/components/altiora/PremiumUI";

/**
 * ALTIORA · Studio Clients (Pro)
 *
 * Vue dédiée aux professionnels et institutions pour :
 *   - Ouvrir un dossier pour un nouveau client (mode "shadow" — le client
 *     n'a pas besoin d'avoir de compte)
 *   - Voir la liste de leurs dossiers-clients
 *   - Étudier chaque client via le parcours économico-social (questionnaire
 *     enrichi), lancer les modules d'analyse et générer le diagnostic ALTIORA
 */
export default function ClientsStudio() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dossiers/my-clients");
      setItems(data.items || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        altioraToast.error("Accès réservé aux professionnels ALTIORA.");
        navigate("/app");
      } else {
        altioraToast.error("Chargement impossible.");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = q.trim()
    ? items.filter((c) => {
        const s = q.trim().toLowerCase();
        // Split display_name into words so "Romain", "TOUSSE" or "MELATAGUIA" all match
        const tokens = ((c.display_name || "") + " " + (c.email || "") + " " + (c.city || ""))
          .toLowerCase();
        return tokens.includes(s);
      })
    : items;

  const active = items.filter((c) => c.status === "active").length;
  const drafts = items.filter((c) => c.status === "draft").length;

  return (
    <PageShell testid="clients-studio-root">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <PageHeader
          eyebrow="ALTIORA · Espace Pro"
          title="Studio Clients"
          subtitle="Ouvrez un dossier client et étudiez son parcours économico-social selon la méthode ALTIORA."
          testid="clients-studio-header" />

        <div className="space-y-6 mt-4">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Clients suivis" value={items.length} />
          <Kpi label="Actifs" value={active} />
          <Kpi label="Brouillons" value={drafts} />
          <Kpi label="Institutions" value={items.filter((c) => c.type === "legal_entity").length} />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B8B8F]" strokeWidth={1.8} />
            <input
              data-testid="clients-search"
              placeholder="Rechercher un client — prénom, nom, email ou ville…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
              className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none pl-9 pr-3 py-2.5 text-sm text-[#1F2937]"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                data-testid="clients-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full text-[#8B8B8F] hover:text-[#0B2545] hover:bg-[#F1EFE8] flex items-center justify-center"
                aria-label="Effacer la recherche"
              ><X className="h-3.5 w-3.5" strokeWidth={2} /></button>
            )}
          </div>
          {q && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
              {filtered.length} résultat{filtered.length > 1 ? "s" : ""}
            </div>
          )}
          <button
            data-testid="clients-new"
            onClick={() => navigate("/app/dossiers/new?for_client=1")}
            className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] text-white font-medium px-5 py-2.5 text-sm transition-colors"
          >
            <PlusCircle className="h-4 w-4" strokeWidth={1.8} />
            Ouvrir un dossier client
          </button>
        </div>

        {/* Explainer */}
        <div className="rounded-md border border-[#E4E1D8] bg-[#FDFCF7] px-5 py-4 flex gap-4">
          <div className="shrink-0 h-9 w-9 rounded-full bg-[#DCC29A]/25 flex items-center justify-center">
            <ClipboardList className="h-4 w-4 text-[#0B2545]" strokeWidth={1.8} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-[#0B2545]">La méthode ALTIORA en 4 étapes</div>
            <ol className="mt-2 text-xs text-[#3A3A3F] space-y-1 list-decimal list-inside">
              <li>Ouvrez un dossier client (aucun compte requis pour lui, vous restez seul propriétaire).</li>
              <li>Complétez le <span className="font-medium text-[#0B2545]">Parcours économico-social</span> — 20+ sections couvrant identité, migration, formation, patrimoine, vulnérabilités.</li>
              <li>Activez les modules d&apos;analyse (Sécurité financière, IMES Trajectoire).</li>
              <li>Générez le diagnostic ALTIORA et le rapport PDF prêt à remettre.</li>
            </ol>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#5B5B62]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" strokeWidth={1.8} /> Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState onCreate={() => navigate("/app/dossiers/new?for_client=1")} hasItems={items.length > 0} q={q} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
            {filtered.map((c) => (
              <ClientCard key={c.dossier_id} c={c} navigate={navigate} />
            ))}
          </div>
        )}
        </div>
      </div>
    </PageShell>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-md border border-[#E4E1D8] bg-white px-5 py-4">
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#8B8B8F]">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-[#0B2545]">{value}</div>
    </div>
  );
}

function ClientCard({ c, navigate }) {
  const [running, setRunning] = useState(false);
  const [runningImes, setRunningImes] = useState(false);
  const initials = ((c.display_name || "?").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("") || "?").toUpperCase();

  const runDiagnostic = async (e) => {
    e.stopPropagation();
    setRunning(true);
    try {
      if (!(c.modules_active || []).includes("securite_financiere")) {
        try { await api.post(`/dossiers/${c.dossier_id}/modules/securite_financiere/activate`); }
        catch { /* already active or minor permission — the analyze call will surface any real issue */ }
      }
      await api.post(`/modules/securite_financiere/analyze/${c.dossier_id}`);
      altioraToast.success("Diagnostic Sécurité financière lancé. Vous allez suivre son avancement.");
      navigate(`/app/dossiers/${c.dossier_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      altioraToast.error(typeof detail === "string" ? detail : "Impossible de lancer le diagnostic.");
    } finally {
      setRunning(false);
    }
  };

  const runImes = async (e) => {
    e.stopPropagation();
    setRunningImes(true);
    try {
      const { data } = await api.post(`/diagnostic/dossier/${c.dossier_id}/analyze`);
      const score = data?.scores?.imes;
      altioraToast.success(score != null
        ? `Diagnostic IMES™ calculé — score ${Number(score).toFixed(1)}/100. L'analyse IA se termine en arrière-plan.`
        : "Diagnostic IMES™ lancé.");
      navigate(`/app/dossiers/${c.dossier_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      altioraToast.error(typeof detail === "string" ? detail
        : "Impossible de lancer l'IMES — complétez d'abord le parcours du client.");
    } finally {
      setRunningImes(false);
    }
  };

  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
      data-testid={`client-card-${c.dossier_id}`}
      onClick={() => navigate(`/app/dossiers/${c.dossier_id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") navigate(`/app/dossiers/${c.dossier_id}`); }}
      className="cursor-pointer rounded-md border border-[#E4E1D8] bg-white p-5 hover:border-[#0B2545] hover:shadow-sm transition-all focus:outline-none focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545]"
    >
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-full bg-[#0B2545] text-white flex items-center justify-center font-mono text-sm">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#1F2937] truncate">{c.display_name}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-[#5B5B62]">
            {c.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" strokeWidth={1.8} />{c.city}</span>}
            {c.email && <span className="inline-flex items-center gap-1 truncate"><Mail className="h-3 w-3" strokeWidth={1.8} />{c.email}</span>}
            {c.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" strokeWidth={1.8} />{c.phone}</span>}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
          c.status === "active" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : c.status === "pending_client_approval" ? "border-amber-200 bg-amber-50 text-amber-800"
          : "border-slate-200 bg-slate-50 text-slate-700"
        }`}>{c.status || "draft"}</span>
        {c.is_shadow && (
          <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#DCC29A]/50 bg-[#FDF7EC] text-[#8A5A00]">
            Shadow client
          </span>
        )}
        {(c.modules_active || []).slice(0, 2).map((m) => (
          <span key={m} className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#F2EFE7] text-[#0B2545]">{m}</span>
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-[#F2EFE7] grid grid-cols-5 gap-2">
        <button
          data-testid={`client-open-parcours-${c.dossier_id}`}
          onClick={(e) => { e.stopPropagation(); navigate(`/app/dossiers/${c.dossier_id}/questionnaire`); }}
          className="inline-flex flex-col items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#0B2545] hover:text-[#DCC29A] transition-colors"
        >
          <ClipboardList className="h-4 w-4" strokeWidth={1.8} />
          Parcours
        </button>
        <button
          data-testid={`client-run-imes-${c.dossier_id}`}
          onClick={runImes}
          disabled={runningImes}
          className="inline-flex flex-col items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#0B2545] hover:text-[#DCC29A] transition-colors disabled:opacity-50 disabled:cursor-wait"
          title="Lancer le diagnostic IMES™ (score de mobilité économico-sociale)"
        >
          {runningImes ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} /> : <Sparkles className="h-4 w-4" strokeWidth={1.8} />}
          IMES™
        </button>
        <button
          data-testid={`client-run-diagnostic-${c.dossier_id}`}
          onClick={runDiagnostic}
          disabled={running}
          className="inline-flex flex-col items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#0B2545] hover:text-[#DCC29A] transition-colors disabled:opacity-50 disabled:cursor-wait"
          title="Lancer le diagnostic Sécurité financière"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} /> : <BadgeCheck className="h-4 w-4" strokeWidth={1.8} />}
          Sécurité
        </button>
        <button
          data-testid={`client-open-modules-${c.dossier_id}`}
          onClick={(e) => { e.stopPropagation(); navigate(`/app/dossiers/${c.dossier_id}`); }}
          className="inline-flex flex-col items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#0B2545] hover:text-[#DCC29A] transition-colors"
        >
          <BadgeCheck className="h-4 w-4" strokeWidth={1.8} />
          Analyse
        </button>
        <button
          data-testid={`client-open-report-${c.dossier_id}`}
          onClick={(e) => { e.stopPropagation(); navigate(`/app/dossiers/${c.dossier_id}`); }}
          className="inline-flex flex-col items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#0B2545] hover:text-[#DCC29A] transition-colors"
        >
          <FileText className="h-4 w-4" strokeWidth={1.8} />
          Rapport
        </button>
      </div>
    </motion.div>
  );
}

function EmptyState({ onCreate, hasItems, q }) {
  if (hasItems && q) {
    return (
      <div className="rounded-md border border-dashed border-[#E4E1D8] bg-white/40 px-6 py-12 text-center">
        <div className="text-sm font-medium text-[#0B2545]">Aucun client ne correspond à « {q} »</div>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-dashed border-[#E4E1D8] bg-white/40 px-6 py-16 text-center">
      <div className="mx-auto h-12 w-12 rounded-full bg-[#F2EFE7] flex items-center justify-center mb-4">
        <Users className="h-5 w-5 text-[#0B2545]" strokeWidth={1.6} />
      </div>
      <div className="text-base font-medium text-[#0B2545]">Pas encore de client suivi</div>
      <p className="mt-2 text-sm text-[#5B5B62] max-w-md mx-auto">
        Ouvrez votre premier dossier client. Vous pourrez tout de suite commencer à documenter son
        parcours économico-social, sans lui demander de créer un compte.
      </p>
      <button
        data-testid="clients-empty-cta"
        onClick={onCreate}
        className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] text-white font-medium px-5 py-2.5 text-sm transition-colors"
      >
        <PlusCircle className="h-4 w-4" strokeWidth={1.8} />
        Ouvrir un premier dossier client
        <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
      </button>
    </div>
  );
}
